import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { ENV_VARS } from "@/constants/env";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type { IPublicRsvpResponse } from "@/types/(company)/events.interface";

// Public, unauthenticated base query — external invitees RSVP via an opaque
// token in the email link, with no user JWT in play.
const publicBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  return fetchBaseQuery({ baseUrl: ENV_VARS.API_INSTANCE })(
    args,
    api,
    extraOptions,
  );
};

// BE wraps responses with { success, statusCode, message, data, timestamp } —
// unwrap `.data` for typed payloads.
const unwrap = <T,>(res: any): T =>
  (res && typeof res === "object" && "data" in res ? res.data : res) as T;

export const eventRsvpApi = createApi({
  reducerPath: "eventRsvpApi",
  baseQuery: publicBaseQuery,
  tagTypes: ["EventRsvp"],
  endpoints: (builder) => ({
    getEventRsvp: builder.query<IPublicRsvpResponse, { token: string }>({
      query: ({ token }) => ({
        url: APIEndpoints.PublicEvents.GetRsvp(token),
      }),
      transformResponse: (res) => unwrap<IPublicRsvpResponse>(res),
      providesTags: ["EventRsvp"],
    }),
    submitEventRsvp: builder.mutation<
      { status: string },
      { token: string; action: "accept" | "decline"; note?: string }
    >({
      query: ({ token, action, note }) => ({
        url: APIEndpoints.PublicEvents.SubmitRsvp(token),
        method: "POST",
        body: { action, note },
      }),
      transformResponse: (res) => unwrap<{ status: string }>(res),
      invalidatesTags: ["EventRsvp"],
    }),
  }),
});

export const { useGetEventRsvpQuery, useSubmitEventRsvpMutation } = eventRsvpApi;
